import React, { useEffect, useState } from 'react'
import { TextInput, TextInputProps, View, ViewStyle } from 'react-native'

import { colors, Icon, XStack } from '@zet/design-system'

import RupeeIcon from '../../assets/icons/RupeeIcon'
import CrossRed from '../../assets/icons/crossRed'
import { M14, R14 } from '../typography'

interface IProps {
  label: string
  placeholder: string
  onChange: (e: string) => void
  onBlur: () => void
  value: string
  keyboardType?: TextInputProps['keyboardType']
  maxLength?: number
  example: string
  isTouched?: boolean
  errorMessage?: string
  isEditable?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  style?: ViewStyle
  pointerEvents?: 'auto' | 'none' | 'box-none' | 'box-only'
  code?: string
  isAmount?: boolean
  isFocused?: boolean // External focus control for dropdowns
}

export const InputWithLabel: React.FC<IProps> = ({
  label,
  placeholder,
  onChange = () => null,
  onBlur,
  value,
  keyboardType,
  maxLength,
  example,
  errorMessage,
  isTouched = false,
  isEditable = true,
  iconRight,
  iconLeft,
  style,
  pointerEvents,
  code,
  isAmount = false,
  isFocused: externalIsFocused,
}) => {
  useEffect(() => {
    if (isAmount && value) {
      handleChange(value)
    }
  }, [value])

  const formatIndianCurrency = (numStr: string) => {
    const cleaned = numStr.replace(/[^0-9]/g, '')
    if (!cleaned) return ''
    const num = parseInt(cleaned, 10)
    if (isNaN(num)) return ''
    const x =
      cleaned.length > 3
        ? cleaned.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',') +
          ',' +
          cleaned.slice(-3)
        : cleaned
    return x
  }

  const [internalIsFocused, setInternalIsFocused] = useState(false)
  const isFocused =
    externalIsFocused !== undefined ? externalIsFocused : internalIsFocused

  const handleChange = (text: string) => {
    const formatted = formatIndianCurrency(text)
    onChange(formatted)
  }

  const handleFocus = () => {
    if (externalIsFocused === undefined) {
      setInternalIsFocused(true)
    }
  }

  const handleBlur = () => {
    if (externalIsFocused === undefined) {
      setInternalIsFocused(false)
    }
    onBlur()
  }

  const getLeftIcon = (icon: string) => {
    switch (icon) {
      case 'CurrencyIcon':
        return <RupeeIcon width={12} className="mr-2" />
      default:
        return <Icon type={icon} color={colors.grays[80]} className="mr-2" />
    }
  }

  return (
    <View>
      <M14
        className={`mb-2 ${!isFocused && errorMessage ? 'text-error-100' : ''}`}
      >
        {label}
      </M14>

      <View className="relative">
        <View
          className={`flex-row items-center rounded-lg bg-white px-[12px] ${
            isFocused
              ? 'border-[1.5px] border-secondary-150'
              : errorMessage
                ? 'border-[2px] border-error-100'
                : 'border-[1.5px] border-[#191B1E52]'
          }`}
          style={style}
        >
          {iconLeft && getLeftIcon(iconLeft as string)}

          <TextInput
            pointerEvents={pointerEvents}
            editable={isEditable}
            onChangeText={isAmount ? handleChange : onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            keyboardType={keyboardType}
            value={value}
            maxLength={maxLength}
            placeholder={example}
            placeholderTextColor={'#24262BA6'}
            className={`h-[45px] flex-1 rounded-md bg-transparent font-[JioType] outline-none`}
          />

          {iconRight && (
            <Icon
              type={iconRight as string}
              color={colors.grays[80]}
              className="ml-2"
            />
          )}
        </View>
      </View>

      {!isFocused && errorMessage && !isTouched ? (
        <XStack className="mt-2">
          <CrossRed className="mr-2" />
          <R14 className="text-black">{errorMessage}</R14>
        </XStack>
      ) : null}
    </View>
  )
}
